import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import api from '../../redux/features/auth/axiosAuthHelper';
import { type RootState } from '../../redux/store';
import { type ApiError } from '../../services/apiError';
import AccountHistory from './AccountHistory';
import colorConfigs from '../../configs/colorConfigs';
import StatusChip from '../../components/common/StatusChip';
import {
  accountSaveError,
  type CustomerAccount,
  validAccount,
  validDefaults,
  type WorkflowDefaults,
  workflowLabels,
} from './accountApi';

interface Props {
  account: CustomerAccount | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function AccountForm(props: Props) {
  const access = useSelector((state: RootState) => state.auth.access);
  const [defaults, setDefaults] = useState<WorkflowDefaults | null>(props.account);
  const [error, setError] = useState<ApiError | null>(null);
  const [load, setLoad] = useState(0);
  useEffect(() => {
    if (props.account) return;
    const controller = new AbortController();
    setError(null);
    api
      .get('customer-accounts/defaults/', {
        headers: { Authorization: `Bearer ${access}` },
        signal: controller.signal,
        timeout: 30000,
      })
      .then((response) => {
        if (controller.signal.aborted) return;
        if (!validDefaults(response.data)) throw new Error('Invalid defaults response');
        setDefaults(response.data);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) {
          const failureInfo = accountSaveError(failure);
          setError(
            failureInfo && {
              ...failureInfo,
              message:
                failureInfo.status === 401 || failureInfo.status === 403
                  ? failureInfo.message
                  : 'Account defaults could not be loaded. Please try again.',
            },
          );
        }
      });
    return () => controller.abort();
  }, [props.account, access, load]);
  // Mount the editor once its initial values are known; loading cannot reset typed values.
  if (defaults) return <AccountEditor {...props} defaults={defaults} />;
  return (
    <Dialog open onClose={props.onClose} maxWidth="sm" fullWidth aria-labelledby="account-form-title">
      <DialogTitle id="account-form-title" sx={{ color: colorConfigs.tables.headBg, fontWeight: 700 }}>Create Customer Account</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error">{error.message}</Alert>
        ) : (
          <CircularProgress aria-label="Loading account defaults" />
        )}
      </DialogContent>
      <DialogActions>
        {error && error.status !== 401 && error.status !== 403 && (
          <Button onClick={() => setLoad((value) => value + 1)}>Retry</Button>
        )}
        <Button onClick={props.onClose}>Cancel</Button>
        <Button disabled>Save account</Button>
      </DialogActions>
    </Dialog>
  );
}

function AccountEditor({ account, defaults, onClose, onSaved }: Props & { defaults: WorkflowDefaults }) {
  const access = useSelector((state: RootState) => state.auth.access);
  const [error, setError] = useState<ApiError | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [now, setNow] = useState(Date.now());
  const headers = { Authorization: `Bearer ${access}` };
  useEffect(() => {
    if (!error?.retryAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [error?.retryAt]);
  const retrySeconds = Math.max(0, Math.ceil(((error?.retryAt ?? 0) - now) / 1000));
  const denied = error?.status === 401 || error?.status === 403 || error?.status === 404;
  const form = useFormik({
    initialValues: {
      account_number: account?.account_number ?? '',
      account_name: account?.account_name ?? '',
      alcohol_workflow_enabled: defaults?.alcohol_workflow_enabled ?? false,
      alcohol_send_to_lab_enabled: defaults?.alcohol_send_to_lab_enabled ?? false,
      manual_override_enabled: defaults?.manual_override_enabled ?? false,
    },
    validate: (values) => {
      const fields: Record<string, string> = {};
      if (!values.account_number.trim()) fields.account_number = 'Account number is required.';
      if (values.account_number.length > 255) fields.account_number = 'Use at most 255 characters.';
      if (values.account_name.length > 255) fields.account_name = 'Use at most 255 characters.';
      return fields;
    },
    onSubmit: async (values) => {
      setError(null);
      try {
        const body = { ...values, account_name: values.account_name.trim() || null };
        const response = account
          ? await api.patch(`customer-accounts/${account.id}/`, body, { headers, timeout: 30000 })
          : await api.post('customer-accounts/', body, { headers, timeout: 30000 });
        if (!validAccount(response.data)) throw new Error('Invalid account response');
        onSaved();
      } catch (failure) {
        setError(accountSaveError(failure));
      }
    },
  });
  const archive = async () => {
    if (!account) return;
    setArchiving(true);
    setError(null);
    try {
      const response = await api.post(
        `customer-accounts/${account.id}/${account.is_archived ? 'restore' : 'archive'}/`,
        {},
        { headers, timeout: 30000 },
      );
      if (!validAccount(response.data)) throw new Error('Invalid account response');
      onSaved();
    } catch (failure) {
      setError(accountSaveError(failure));
    } finally {
      setArchiving(false);
      setConfirmArchive(false);
    }
  };
  const busy = form.isSubmitting || archiving;
  const disabled = busy || denied || retrySeconds > 0;
  const fieldError = (key: string) => error?.fields[key] || form.errors[key as keyof typeof form.values];
  return (
    <>
      <Dialog
        open
        onClose={busy ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="account-form-title"
      >
        <DialogTitle id="account-form-title" sx={{ color: colorConfigs.tables.headBg, fontWeight: 700 }}>
          {account ? 'Edit Customer Account' : 'Create Customer Account'}
        </DialogTitle>
        <Box component="form" onSubmit={form.handleSubmit} noValidate>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && (
              <Alert severity="error">
                {error.message}
                {Object.entries(error.fields)
                  .filter(([key]) => !(key in form.values))
                  .map(([key, message]) => ` ${key}: ${message}`)
                  .join('')}
                {retrySeconds > 0 && ` Retry in ${retrySeconds}s.`}
              </Alert>
            )}
            <>
              {account?.is_archived && (
                <Alert severity="info">
                  This account is archived. Restore it before editing its configuration.
                </Alert>
              )}
              <TextField
                label="Account Number"
                size="small"
                name="account_number"
                value={form.values.account_number}
                onChange={form.handleChange}
                required
                disabled={disabled || !!account}
                error={!!fieldError('account_number')}
                helperText={
                  fieldError('account_number') ||
                  (account
                    ? 'Account number cannot be changed.'
                    : 'Use the location code exactly as supplied by Elkay.')
                }
              />
              <TextField
                label="Account Name"
                size="small"
                name="account_name"
                value={form.values.account_name}
                onChange={form.handleChange}
                disabled={disabled || account?.is_archived}
                error={!!fieldError('account_name')}
                helperText={fieldError('account_name') || (account ? undefined : 'Optional')}
              />
              {(Object.keys(workflowLabels) as (keyof WorkflowDefaults)[]).map((key) => (
                <Box key={key}>
                  <FormControlLabel
                    label={workflowLabels[key]}
                    control={
                      <Switch
                        name={key}
                        checked={form.values[key]}
                        onChange={form.handleChange}
                        disabled={disabled || account?.is_archived}
                      />
                    }
                  />
                  {fieldError(key) && (
                    <Typography color="error" variant="body2">
                      {fieldError(key)}
                    </Typography>
                  )}
                </Box>
              ))}
            </>
            {account && (
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, borderTop: '1px solid #e1e8e6', pt: 1 }}>
                <AccountHistory accountId={account.id} />
                <StatusChip enabled={!account.is_archived} trueLabel="Active" falseLabel="Archived" />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ mx: 3, px: 0, pt: 1.5, pb: 2, borderTop: '1px solid #e1e8e6' }}>
            {account && (
              <Button
                color={account.is_archived ? 'primary' : 'error'}
                disabled={disabled}
                onClick={() => setConfirmArchive(true)}
                sx={{ mr: 'auto' }}
              >
                {account.is_archived ? 'Restore account' : 'Archive account'}
              </Button>
            )}
            <Button disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            {!account?.is_archived && (
              <Button type="submit" variant="contained" disabled={!defaults || disabled}>
                {form.isSubmitting ? 'Saving…' : 'Save account'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog
        open={confirmArchive}
        onClose={archiving ? undefined : () => setConfirmArchive(false)}
        aria-labelledby="account-archive-title"
      >
        <DialogTitle id="account-archive-title">
          {account?.is_archived ? 'Restore' : 'Archive'} account?
        </DialogTitle>
        <DialogContent>
          {account?.is_archived
            ? 'The app will use this stored configuration again.'
            : 'The app will use defaults for this account. Its record and audit history will be retained.'}
        </DialogContent>
        <DialogActions>
          <Button disabled={archiving} onClick={() => setConfirmArchive(false)}>
            Cancel
          </Button>
          <Button disabled={disabled} onClick={() => void archive()}>
            {account?.is_archived ? 'Restore' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
